import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authService, type User } from '@/services/authService';
import { clearUserProfileCache } from '@/contexts/UserProfileContext';

interface UseAuthReturn {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: { email: string; password: string; rememberMe?: boolean }) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  register: (credentials: {
    email: string;
    name: string;
    password: string;
    password_confirmation: string;
  }) => Promise<void>;
  logout: (options?: { allDevices?: boolean }) => Promise<void>;
}

const useAuth = (): UseAuthReturn => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => authService.isAuthenticated());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const authenticated = authService.isAuthenticated();
        setIsAuthenticated(authenticated);
        if (authenticated) {
          const currentUser = authService.getCurrentUser();
          setUser(currentUser);
        }
      } catch (err) {
        console.error('Failed to initialize auth:', err);
        setError('Failed to initialize authentication');
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const register = useCallback(
    async (credentials: {
      email: string;
      name: string;
      password: string;
      password_confirmation: string;
    }) => {
      setIsLoading(true);
      setError(null);

      try {
        await authService.register(credentials);
        // Update auth state after successful registration
        setIsAuthenticated(true);
        const currentUser = authService.getCurrentUser();
        setUser(currentUser);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to register';
        setError(errorMessage);
        setIsAuthenticated(false);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const login = useCallback(
    async (credentials: { email: string; password: string; rememberMe?: boolean }) => {
      setIsLoading(true);
      setError(null);

      try {
        await authService.login(credentials);
        // Update auth state after successful login
        setIsAuthenticated(true);
        const currentUser = authService.getCurrentUser();
        setUser(currentUser);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to login';
        setError(errorMessage);
        setIsAuthenticated(false);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const loginWithGoogle = useCallback(async (credential: string) => {
    setIsLoading(true);
    setError(null);

    try {
      await authService.loginWithGoogle(credential);
      // Update auth state after successful login
      setIsAuthenticated(true);
      const currentUser = authService.getCurrentUser();
      setUser(currentUser);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to login with Google';
      setError(errorMessage);
      setIsAuthenticated(false);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async (options: { allDevices?: boolean } = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      // Clear UserProfileContext cache FIRST (synchronously) to prevent race conditions
      clearUserProfileCache();

      // AL-1779: awaited, because the logout now revokes the token on the
      // server. Resolving before that lands would let the UI say the session
      // ended while the token was still good.
      await authService.logout(options);

      // Clear all React Query caches to prevent stale data
      queryClient.clear();

      setIsAuthenticated(false);
      setUser(null);
    } catch (err) {
      console.error('Error during logout:', err);
      setError('Failed to logout');
      setIsAuthenticated(false);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [queryClient]);

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    loginWithGoogle,
    register,
    logout,
  };
};

export { useAuth };

// Export types for external use
export type { User } from '@/services/authService';
